import React from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiRefreshCcw, FiShield, FiClock, FiEdit3, FiMessageCircle, FiHeart, FiDollarSign, FiTruck } = FiIcons;

const PolicyDocument = ({ settings }) => {
  if (!settings || Object.keys(settings).length === 0) {
    return (
      <div className="text-center py-20 bg-slate-50 rounded-[3rem] border border-dashed border-slate-200">
        <SafeIcon icon={FiShield} className="text-5xl text-slate-300 mx-auto mb-6" />
        <h3 className="text-xl font-black text-slate-900 mb-2">Standard Policies Apply</h3>
        <p className="text-lg text-slate-500 font-medium">This seller has not customized their policies yet.</p>
      </div>
    );
  }

  const isPlatform = settings.paymentMode === 'platform';

  const sections = [
    {
      icon: FiHeart,
      title: 'Quality Guarantee',
      content: settings.qualityPromise === '100_satisfaction' 
        ? 'We stand behind our products with a 100% satisfaction guarantee. If you are not completely happy with the quality or taste of your order, we promise to make it right.'
        : 'All our items are made fresh to order using high-quality ingredients. We maintain strict hygiene standards to ensure the best possible experience for our neighbors.',
      color: 'text-rose-600',
      bg: 'bg-rose-50'
    },
    {
      icon: FiRefreshCcw,
      title: 'Returns Policy',
      content: settings.returnsAccepted 
        ? `We accept returns within ${settings.returnTimeframe} of fulfillment for quality-related issues. Items must be returned with at least 75% of the product remaining in its original packaging.`
        : 'Due to strict food safety standards and the perishable nature of our homemade goods, we cannot accept physical returns once an order has been fulfilled and accepted.',
      color: 'text-indigo-600',
      bg: 'bg-indigo-50'
    },
    {
      icon: FiDollarSign,
      title: 'Refund Policy',
      content: (() => {
        let base = "";
        if (settings.refundType === 'full') base = "Valid quality issues are eligible for a full refund. ";
        else if (settings.refundType === 'partial') base = "Depending on the issue, we may offer a partial refund. ";
        else if (settings.refundType === 'credit') base = "We provide store credit for future orders in the event of a quality concern. ";
        else base = "We prioritize replacements for any items that don't meet our standards. ";
        
        const wording = isPlatform 
          ? "Refunds are processed automatically through the platform back to your original payment method."
          : "Refunds are handled directly by the seller via the original payment method (e.g., Cash, e-Transfer, PayPal, Square).";
        return base + wording;
      })(),
      color: 'text-emerald-600',
      bg: 'bg-emerald-50'
    },
    {
      icon: FiTruck,
      title: 'Delivery & Pickup',
      content: settings.deliveryPolicy === 'strict'
        ? 'Please arrive on time for your scheduled window. If you are more than 30 minutes late without notice, we may need to reschedule fulfillment for a later time.'
        : 'We offer a flexible fulfillment window. If your plans change, please message us as soon as possible to coordinate a new time.',
      color: 'text-cyan-600',
      bg: 'bg-cyan-50'
    },
    {
      icon: FiClock,
      title: 'Cancellation Policy',
      content: settings.cancelWindow === 'none'
        ? 'Since items are prepared specifically for you, we cannot accept cancellations once an order has been officially accepted by the baker.'
        : `Free cancellations are permitted within ${settings.cancelWindow} of the order being accepted. After this window, cancellations may incur a fee to cover ingredient costs.`,
      color: 'text-amber-600',
      bg: 'bg-amber-50'
    },
    {
      icon: FiEdit3,
      title: 'Order Changes',
      content: settings.changeWindow === 'none'
        ? 'Modifications to orders (flavors, quantities, or dates) cannot be made after the baker has accepted the request.'
        : `We can accommodate changes to your order within ${settings.changeWindow} of the order being accepted. Please message us as soon as possible to request modifications.`,
      color: 'text-blue-600',
      bg: 'bg-blue-50'
    },
    {
      icon: FiMessageCircle,
      title: 'Issue Resolution',
      content: `If there is an issue with your order, please contact us immediately through the SohLocal chat. We aim to respond to all concerns within ${settings.responseTime} and will work with you to find a fair solution.`,
      color: 'text-purple-600',
      bg: 'bg-purple-50'
    }
  ];

  return (
    <div className="space-y-6">
      {sections.map((section, idx) => (
        <div key={idx} className="flex items-start p-8 bg-white rounded-[2rem] border border-slate-100 shadow-sm hover:border-indigo-200 transition-all group">
          <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mr-6 flex-shrink-0 transition-transform group-hover:scale-110 ${section.bg} ${section.color}`}>
            <SafeIcon icon={section.icon} className="text-2xl" />
          </div>
          <div className="flex-1">
            <h4 className="text-sm font-black text-slate-900 uppercase tracking-[0.15em] mb-2">{section.title}</h4>
            <p className="text-lg text-slate-700 leading-relaxed font-medium">{section.content}</p>
          </div>
        </div>
      ))}

      <div className="pt-10 border-t border-slate-100 flex items-center justify-center text-slate-400">
        <SafeIcon icon={FiShield} className="mr-3 text-lg" />
        <span className="text-[10px] font-black uppercase tracking-[0.3em]">
          Official Store Policy {isPlatform && '• Verified Neighbor'}
        </span>
      </div>
    </div>
  );
};

export default PolicyDocument;